import React, { useMemo, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { useVoucherCheck } from '~/hooks/apis/promo/useVoucherCheck';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import type { CheckVoucherCodeQuery, CheckVoucherCodeResponse } from '~/services/PromoService';

type VoucherCheckProps = {
  voucherName: string;
};

/** Approximate windows for usage-count queries (API uses hours). */
const COUNTS_WINDOW_PRESETS = [
  { label: '24 hours', hours: 24 },
  { label: '1 week', hours: 7 * 24 },
  { label: '1 month', hours: 30 * 24 },
  { label: '12 months', hours: 365 * 24 },
] as const;

function formatCountsLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function voucherStringSection(title: string, content: string): React.ReactNode {
  if (!content?.trim()) {
    return null;
  }
  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0 }}>
        {content}
      </Typography>
    </Box>
  );
}

function renderResult(result: CheckVoucherCodeResponse['result']) {
  const { voucherString, counts, _links } = result;

  return (
    <Stack spacing={2}>
      <Stack spacing={1.5}>
        {voucherStringSection('Description', voucherString.voucher_description)}
        {voucherStringSection('Conditions', voucherString.voucher_conditions)}
        {voucherStringSection('Rewards', voucherString.voucher_rewards)}
        {voucherStringSection('Warnings', voucherString.voucher_warnings)}
      </Stack>
      {counts && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Usage counts
            {typeof counts.since_hours === 'number' ? ` (last ${counts.since_hours}h)` : null}
          </Typography>
          <Table size="small" sx={{ maxWidth: 480 }}>
            <TableBody>
              {Object.entries(counts).map(([key, value]) => (
                <TableRow key={key}>
                  <TableCell sx={{ border: 0, pl: 0, py: 0.5 }}>{formatCountsLabel(key)}</TableCell>
                  <TableCell align="right" sx={{ border: 0, py: 0.5 }}>
                    {value}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {_links?.length > 0 && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Links
          </Typography>
          <Stack spacing={0.5}>
            {_links.map((link) => (
              <Link
                key={`${link.rel}-${link.href}`}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                {link.title || link.rel}
                <OpenInNewIcon sx={{ fontSize: 14 }} aria-hidden />
              </Link>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

/** API allows 1–8760 hours for usage counts window. */
const COUNTS_SINCE_HOURS_MIN = 1;
const COUNTS_SINCE_HOURS_MAX = 8760;

function parseCountsSinceHours(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return undefined;
  }
  const n = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(n) || n < COUNTS_SINCE_HOURS_MIN || n > COUNTS_SINCE_HOURS_MAX) {
    return undefined;
  }
  return n;
}

export default function VoucherCheck({ voucherName }: VoucherCheckProps) {
  const { tenant } = useCurrentTenant();
  const brand = tenant.brand;
  const [includeCounts, setIncludeCounts] = useState(false);
  const [countsSinceHoursInput, setCountsSinceHoursInput] = useState('');
  const [presentationType, setPresentationType] = useState<CheckVoucherCodeQuery['presentationType'] | ''>('');

  const countsSinceHours = useMemo(() => parseCountsSinceHours(countsSinceHoursInput), [countsSinceHoursInput]);
  const hoursInputInvalid = countsSinceHoursInput.trim() !== '' && countsSinceHours === undefined;

  const { data, isLoading, isError, error, isFetching, refetch } = useVoucherCheck({
    voucherName,
    brand: brand ?? undefined,
    includeCounts,
    ...(presentationType !== '' && presentationType !== undefined ? { presentationType } : {}),
    ...(includeCounts && countsSinceHours !== undefined ? { countsSinceHours } : {}),
  });

  const message = error instanceof Error ? error.message : 'Request failed';

  const paramSummaryParts: Array<string> = [];
  if (includeCounts) {
    paramSummaryParts.push('includeCounts');
    if (countsSinceHours !== undefined) {
      paramSummaryParts.push(`countsSinceHours=${countsSinceHours}`);
    }
  }
  if (presentationType) {
    paramSummaryParts.push(`presentationType=${String(presentationType)}`);
  }
  const paramSummary =
    paramSummaryParts.length > 0 ? paramSummaryParts.join(', ') : 'defaults (no optional query params)';

  return (
    <Accordion defaultExpanded={false} variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Voucher check</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          <Typography variant="body1">
            (Voucher check is designed for consumption AI models and applications)
          </Typography>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Query: {paramSummary}
          </Typography>

          <Stack spacing={2} sx={{ mb: 2 }}>
            <FormControl component="fieldset" variant="standard">
              <FormLabel component="legend" sx={{ fontWeight: 'bold', fontSize: '0.875rem', mb: 1 }}>
                Check options
              </FormLabel>
              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2}>
                <FormControlLabel
                  control={
                    <Switch checked={includeCounts} onChange={(e) => setIncludeCounts(e.target.checked)} size="small" />
                  }
                  label="Include usage counts"
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    void refetch();
                  }}
                  disabled={!voucherName.trim()}
                >
                  Refresh
                </Button>
              </Stack>
            </FormControl>

            <Stack spacing={1}>
              <FormLabel sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Counts window (hours)</FormLabel>
              <TextField
                type="number"
                size="small"
                placeholder="Optional — omit for API default"
                value={countsSinceHoursInput}
                onChange={(e) => setCountsSinceHoursInput(e.target.value)}
                disabled={!includeCounts}
                error={hoursInputInvalid}
                helperText={
                  hoursInputInvalid
                    ? `Enter a whole number ${COUNTS_SINCE_HOURS_MIN}–${COUNTS_SINCE_HOURS_MAX} or leave empty`
                    : 'Only used when usage counts are included'
                }
                inputProps={{ min: COUNTS_SINCE_HOURS_MIN, max: COUNTS_SINCE_HOURS_MAX, step: 1 }}
                sx={{ maxWidth: 280 }}
              />
              <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
                {COUNTS_WINDOW_PRESETS.map((preset) => (
                  <Button
                    key={preset.label}
                    variant="outlined"
                    size="small"
                    disabled={!includeCounts}
                    onClick={() => setCountsSinceHoursInput(String(preset.hours))}
                  >
                    {preset.label}
                  </Button>
                ))}
              </Stack>
            </Stack>

            <FormControl size="small" sx={{ maxWidth: 320 }}>
              <InputLabel id="voucher-check-presentation-type-label">Presentation type</InputLabel>
              <Select
                labelId="voucher-check-presentation-type-label"
                label="Presentation type"
                value={presentationType === undefined || presentationType === '' ? '' : presentationType}
                onChange={(e) => {
                  const v = e.target.value;
                  setPresentationType(v);
                }}
              >
                <MenuItem value="">
                  <Typography component="span" variant="body2" sx={{ fontStyle: 'italic' }}>
                    Default (omit)
                  </Typography>
                </MenuItem>
                <MenuItem value="short">short</MenuItem>
                <MenuItem value="medium">medium</MenuItem>
                <MenuItem value="long">long</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Optional string length preset for voucher copy (contract: short | medium | long).
              </Typography>
            </FormControl>
          </Stack>

          {isLoading && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading check result…</Typography>
            </Box>
          )}
          {isError && <Alert severity="error">{message}</Alert>}
          {!isLoading && !isError && data?.result && renderResult(data.result)}
          {!isLoading && !isError && !data?.result && (
            <Typography variant="body2" color="text.secondary">
              No result returned.
            </Typography>
          )}
          {isFetching && !isLoading && (
            <Box mt={1} display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Refreshing…
              </Typography>
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
